/**
 * The one sanitizer for question labels.
 *
 * A label is HTML wherever a question is shown (the public form, the legacy landing-page modal, the
 * landing-page form modal, both builders, the form details email), so every one of those places
 * renders {@link sanitizeFieldLabel}'s output, never the stored string.
 *
 * Built on `sanitize-html` (pure JS) rather than `isomorphic-dompurify`: that one pulls jsdom into
 * server-render bundles, which 500s pages on a cold start (see `src/lib/utils/svg-sanitization.ts`),
 * and plain `dompurify` cannot sanitize without a DOM. The same code runs on the server and in the
 * browser, so the output is identical in both.
 */

import sanitizeHtml from "sanitize-html";

/** Tags a label may keep: inline formatting, links and line breaks. */
const LABEL_ALLOWED_TAGS = ["b", "strong", "em", "i", "u", "a", "br", "span"];

/** Attributes a label's links may keep. No other tag keeps any attribute. */
const LABEL_ALLOWED_LINK_ATTRIBUTES = ["href", "target", "rel"];

/**
 * Attributes a link may carry in a stored label even though {@link sanitizeFieldLabel} drops them
 * on display. The v1 builder's link tool writes an inline `style` onto every link it inserts, and
 * labels written with it before the label editor became a plain input still hold that style; they
 * are answer keys, so the save check keeps them as written. Display never renders the style.
 */
const LINK_ATTRIBUTES_KEPT_ON_SAVE = ["style"];

/** Link schemes a label may use. Relative and anchor links have no scheme and pass. */
const LABEL_ALLOWED_SCHEMES = ["http", "https", "mailto", "tel"];

/** sanitize-html options for a label. Everything outside the allowlists is dropped; text is kept. */
const LABEL_SANITIZE_OPTIONS: sanitizeHtml.IOptions = {
  allowedTags: LABEL_ALLOWED_TAGS,
  allowedAttributes: { a: LABEL_ALLOWED_LINK_ATTRIBUTES },
  allowedSchemes: LABEL_ALLOWED_SCHEMES,
  disallowedTagsMode: "discard",
};

/**
 * Comments, doctypes, CDATA and processing instructions. The parser drops these without reporting
 * them as tags, so the markup comparison below cannot see them; a label holding one is sanitized.
 */
const MARKUP_DECLARATION_REGEX = /<[!?]/;

/**
 * Plain text appended to a label before parsing. It survives only when the label ends in text; a
 * label that ends inside a tag, an attribute or a comment (`Name<img src=x onerror=...` with no
 * closing `>`) swallows it, and the parser then drops the whole fragment without reporting it.
 */
const END_OF_LABEL_MARKER = "endoflabelmarker";

/**
 * Sanitizes a label for display.
 * @param html - The label as stored.
 * @returns The label with every tag, attribute and link scheme outside the allowlist removed, and
 *   its text entity-escaped. Safe to pass to `dangerouslySetInnerHTML` or an email body.
 */
export function sanitizeFieldLabel(html: string): string {
  if (!html) return "";
  return sanitizeHtml(html, LABEL_SANITIZE_OPTIONS);
}

/**
 * Sanitizes a label for saving, keeping the stored string byte-for-byte when it holds only allowed
 * markup. Labels are the keys answers are stored under, so rewriting a safe label (escaping its `&`,
 * turning `<br>` into `<br />`) would orphan every answer already given to it. A link's
 * `LINK_ATTRIBUTES_KEPT_ON_SAVE` count as allowed here; display still drops them.
 * @param label - The label about to be saved.
 * @returns The same string when it is already safe, otherwise {@link sanitizeFieldLabel}'s output.
 */
export function sanitizeFieldLabelForSave(label: string): string {
  return hasOnlyAllowedMarkup(label) ? label : sanitizeFieldLabel(label);
}

/**
 * Whether a label holds only allowed markup, so that sanitizing it would change how it is written
 * (escaped text, re-quoted attributes) but remove nothing. Compares the markup the parser sees in
 * the label with the markup it sees in the sanitized output: every open tag with all of its
 * attributes, in order. A dropped tag, attribute or link, an `html`/`body` wrapper included, makes
 * them differ. A link's `LINK_ATTRIBUTES_KEPT_ON_SAVE` are left out of the comparison.
 * @param label - The label to check.
 * @returns True when the label holds nothing outside the allowlist.
 */
function hasOnlyAllowedMarkup(label: string): boolean {
  if (MARKUP_DECLARATION_REGEX.test(label)) return false;

  const probe = `${label}${END_OF_LABEL_MARKER}`;
  const input = sanitizeRecordingOpenTags(probe);
  if (countOccurrences(input.sanitized, END_OF_LABEL_MARKER) !== countOccurrences(probe, END_OF_LABEL_MARKER)) {
    return false;
  }

  const output = sanitizeRecordingOpenTags(input.sanitized);
  return (
    input.openTags.length === output.openTags.length &&
    input.openTags.every((openTag, index) => openTag === output.openTags[index])
  );
}

/**
 * Sanitizes a label and records every open tag the parser saw, before anything was dropped.
 * @param html - The HTML to parse.
 * @returns The sanitized HTML and one `[tag, attributes]` entry per open tag in the input, leaving
 *   out a link's `LINK_ATTRIBUTES_KEPT_ON_SAVE`.
 */
function sanitizeRecordingOpenTags(html: string): { sanitized: string; openTags: string[] } {
  const openTags: string[] = [];
  const sanitized = sanitizeHtml(html, {
    ...LABEL_SANITIZE_OPTIONS,
    onOpenTag: (name, attribs) => {
      const compared = Object.entries(attribs).filter(
        ([attribute]) => !(name === "a" && LINK_ATTRIBUTES_KEPT_ON_SAVE.includes(attribute))
      );
      openTags.push(JSON.stringify([name, compared]));
    },
  });
  return { sanitized, openTags };
}

/**
 * Counts the non-overlapping occurrences of a substring.
 * @param text - The text to search.
 * @param search - The substring to count.
 * @returns How many times `search` appears in `text`.
 */
function countOccurrences(text: string, search: string): number {
  return text.split(search).length - 1;
}
